/**
 * Validate a set of tier rows for a template.
 *
 * Rules (owner requirements):
 *   - min_qty on row #1 must be 1
 *   - each row's min_qty must equal previous row's max_qty + 1 (contiguous)
 *   - only the last row may have max_qty = null (∞)
 *   - min_qty <= max_qty when max_qty is not null
 *   - each row must be percent OR fixed (never both, never neither)
 *
 * Returns { ok, errors: [{ index, message }] } — index=-1 for global errors.
 */
export function validateTierRows(rowsRaw) {
  const rows = (rowsRaw || []).map((r) => ({
    min_qty: r.min_qty === "" || r.min_qty === null || r.min_qty === undefined ? NaN : Number(r.min_qty),
    max_qty: r.max_qty === "" || r.max_qty === null || r.max_qty === undefined ? null : Number(r.max_qty),
    percent_off: r.percent_off === "" || r.percent_off === null || r.percent_off === undefined ? null : Number(r.percent_off),
    fixed_price: r.fixed_price === "" || r.fixed_price === null || r.fixed_price === undefined ? null : Number(r.fixed_price),
  }));
  const errors = [];
  if (rows.length === 0) {
    errors.push({ index: -1, message: "Add at least one row." });
    return { ok: false, errors, rows };
  }
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const hasPct = r.percent_off !== null && !Number.isNaN(r.percent_off);
    const hasFix = r.fixed_price !== null && !Number.isNaN(r.fixed_price);
    if (hasPct && hasFix) errors.push({ index: i, message: "Set % off OR fixed price — not both." });
    if (!hasPct && !hasFix) errors.push({ index: i, message: "Set a % off or a fixed price." });
    if (Number.isNaN(r.min_qty) || r.min_qty < 1) {
      errors.push({ index: i, message: "Min must be ≥ 1." });
    }
    if (r.max_qty !== null && !Number.isNaN(r.max_qty) && !Number.isNaN(r.min_qty) && r.max_qty < r.min_qty) {
      errors.push({ index: i, message: "Max must be ≥ min (or blank for ∞)." });
    }
  }
  // First row min must be 1.
  if (!Number.isNaN(rows[0].min_qty) && rows[0].min_qty !== 1) {
    errors.push({ index: 0, message: "First tier must start at 1." });
  }
  // Contiguous check
  for (let i = 1; i < rows.length; i++) {
    const prev = rows[i - 1];
    const cur = rows[i];
    if (prev.max_qty === null) {
      errors.push({ index: i - 1, message: "Only the LAST row may be open-ended (∞)." });
      break;
    }
    if (!Number.isNaN(cur.min_qty) && !Number.isNaN(prev.max_qty) && cur.min_qty !== prev.max_qty + 1) {
      errors.push({
        index: i,
        message: `Must start at ${prev.max_qty + 1} to be contiguous with row above.`,
      });
    }
  }
  return { ok: errors.length === 0, errors, rows };
}

export function hasFixedRows(rowsRaw) {
  return (rowsRaw || []).some((r) => r.fixed_price !== null && r.fixed_price !== undefined && r.fixed_price !== "");
}
